<template>
  <PageView
    :main-title="title"
    :sub-title="subTitle"
    :sub-title-icon="subTitleIcon"
    :display-sub-title-in-green="true"
  >
    <ContentView v-if="grae" class="pr-2">
      <div v-if="section === 'indicateurs'">
        <h1 class="title">{{ $t("IndicateursPage.title") }}</h1>
        <IndicateursView v-if="grae" :grae="grae" />
      </div>
      <div v-if="section === 'details'">
        <GraeDetailsView v-if="grae.id" :grae="grae" :active-tab="activeSubTab" />
      </div>
      <div v-if="section === 'ModifProjet'">
        <ModifProjetView v-if="grae.id" :grae="grae" :active-tab="activeSubTab" />
      </div>
      <div v-else-if="section === 'membres'">
        <GraeMembers v-if="grae.id" :grae-id="grae.id" :logged-user="user" />
      </div>
      <div v-else-if="section === 'actualites'">
        <ActualitesView :grae-id="graeId" />
      </div>
      <div v-else-if="section === 'dates'">
        <GraeDatesView v-if="grae.id" :grae="grae" />
      </div>
      <div v-else-if="section === 'contacts'">
        <GraeContactView :logged-user="user" :grae="grae" />
      </div>
    </ContentView>
  </PageView>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { eventBus } from "@/services/EventBus"
import PageView from "@/views/commons/PageView.vue"
import ContentView from "@/views/commons/ContentView.vue"
import GraeDetailsView from "@/views/grae/GraeDetailsView.vue"
import GraeDatesView from "@/views/grae/GraeDatesView.vue"
import GraeMembers from "@/views/grae/GraeMembers.vue"
import IndicateursView from "@/pages/grae/IndicateursView.vue"
import ModifProjetView from "@/pages/modif_projet/ModifProjetView.vue"
import ActualitesView from "@/views/grae/ActualitesView.vue"
import GraeContactView from "@/pages/grae/GraeContactView.vue"

import { GraeDTO, UtilisateurDTO } from "@/model/bean/GeneratedDTOs"
import { PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"
import { LoginService } from "@/services/rest/login/LoginService"
import { UsersService } from "@/services/rest/users/UsersService"
import { OrganismeService } from "@/services/rest/organisme/OrganismeService"
import {
  BackToPreviousScreenItem,
  ContextMenu,
  NavbarItem,
  NavbarItemWithoutLink,
} from "@/views/menus/MenuItems"

export default defineComponent({
  name: "GraePage",
  components: {
    PageView,
    ContentView,
    GraeDetailsView,
    GraeMembers,
    GraeDatesView,
    ActualitesView,
    IndicateursView,
    GraeContactView,
    ModifProjetView,
  },
  props: {
    graeId: {
      type: String,
      default: "",
    },
    section: {
      type: String,
      default: "indicateurs",
    },
    activeSubTab: {
      type: Number,
      default: 0,
    },
  },

  emits: ["contextual-menu-changed"],
  data() {
    return {
      loginService: LoginService.INSTANCE,
      usersService: UsersService.INSTANCE,
      organismeService: OrganismeService.INSTANCE,
      user: new UtilisateurDTO(),
      grae: new GraeDTO(),
      title: "",
      subTitle: "",
      subTitleIcon: "",
    }
  },
  watch: {
    section(): void {
      this.activeSectionChanged()
    },
  },
  created(): void {
    const loggedUser = this.loginService.getLoggedUser()
    if (!loggedUser || !loggedUser.id) {
      return
    }

    if (!this.graeId) {
      this.getGraeFromLoggedUser(loggedUser.id)
    } else {
      this.getGraeFromId(loggedUser.id, this.graeId)
    }
  },
  methods: {
    async getGraeFromLoggedUser(id: string): Promise<void> {
      this.user = await this.usersService.getUserProfile(id)
      if (this.user && this.user.grae) {
        this.grae = this.user.grae as GraeDTO
      }
      // If user has multi-graes we want to show the back button
      this.refreshContextMenu(this.user.instructedGraes && this.user.instructedGraes.length !== 0)
      this.subTitle = this.$t("graePage.myGrae").toString()
      this.activeSectionChanged()
    },

    async getGraeFromId(loggedUserId: string, graedId: string): Promise<void> {
      this.user = await this.usersService.getUserProfile(loggedUserId)
      this.grae = await this.organismeService.getGrae(graedId)
      const hasOneGrae =
        this.user.grae?.id == this.grae.id &&
        (!this.user.instructedGraes || this.user.instructedGraes.length === 0)
      // If user is from grae but has multi graes, we want to show the back button
      this.refreshContextMenu(!hasOneGrae)
      this.subTitle = hasOneGrae ? this.$t("graePage.myGrae").toString() : this.grae.region
      this.activeSectionChanged()
    },

    refreshContextMenu(showBackToListMenuEntry: boolean): void {
      const menuItems = []
      if (this.grae) {
        const graeId = this.grae.id
        // Back to previous screen (if comming from list)
        if (showBackToListMenuEntry) {
          menuItems.push(new BackToPreviousScreenItem())
        }

        // Current Grae
        menuItems.push(new NavbarItemWithoutLink("GRAE " + this.grae.region, ""))

        // Sub-sections members / actualites / dates
        menuItems.push(
          new NavbarItem(
            "/grae/indicateurs/0/" + graeId,
            this.$t("menu.indicateurs").toString(),
            "chart-pie",
            false,
          ),
        )
        menuItems.push(
          new NavbarItem(
            "/grae/details/0/" + graeId,
            this.$t("graePage.details.title").toString(),
            "signature",
            false,
          ),
        )
        // Add instructions menu entries for acc nat
        if (
          this.usersService.hasAnyProfile(this.user, [
            PROFIL_UTILISATEUR.OBSERVATEUR_NATIONAL,
            PROFIL_UTILISATEUR.ACCOMPAGNATEUR_NATIONAL,
            PROFIL_UTILISATEUR.SUPER_ADMINISTRATEUR,
            PROFIL_UTILISATEUR.ACCOMPAGNATEUR_REGIONAL,
            PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL,
          ])
        ) {
          menuItems.push(
            new NavbarItem(
              `/grae/ModifProjet/0/${graeId}`,
              this.$t("menu.ModifProjet").toString(),
              "calendar",
              false,
            ),
          )
        }
        menuItems.push(
          new NavbarItem(
            "/grae/membres/0/" + graeId,
            this.$t("graePage.membres.title").toString(),
            "users",
            false,
          ),
        )
        menuItems.push(
          new NavbarItem(
            "/grae/actualites/0/" + graeId,
            this.$t("graePage.actualites.title").toString(),
            "newspaper",
            false,
          ),
        )
        menuItems.push(
          new NavbarItem(
            "/grae/dates/0/" + graeId,
            this.$t("graePage.dates.title").toString(),
            "calendar-day",
            false,
          ),
        )

        // Add instructions menu entries for acc nat
        if (
          this.usersService.hasAnyProfile(this.user, [
            PROFIL_UTILISATEUR.OBSERVATEUR_NATIONAL,
            PROFIL_UTILISATEUR.ACCOMPAGNATEUR_NATIONAL,
            PROFIL_UTILISATEUR.SUPER_ADMINISTRATEUR,
          ]) ||
          (this.usersService.hasAnyProfile(this.user, [
            PROFIL_UTILISATEUR.ACCOMPAGNATEUR_REGIONAL,
            PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL,
          ]) &&
            this.user.instructedGraes &&
            this.user.instructedGraes.length !== 0)
        ) {
          menuItems.push(
            new NavbarItem(
              `/instruction/inscriptions/0/0/${graeId}`,
              this.$t("instructionPage.instruction").toString(),
              "calendar",
              false,
            ),
          )
        }

        // Add contact-project-list
        if (
          this.usersService.hasAnyProfile(this.user, [
            PROFIL_UTILISATEUR.ACCOMPAGNATEUR_REGIONAL,
            PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL,
          ]) &&
          this.user?.instructedGraes?.length !== 0
        ) {
          menuItems.push(
            new NavbarItem(
              `/grae/contacts/0/${graeId}`,
              this.$t("menu.contact-porteurs-projet").toString(),
              "users",
              false,
            ),
          )
        }

        const possibleMenuParents = ["/grae-list", "/grae/details/"]
        eventBus.emit("contextual-menu-changed", new ContextMenu(possibleMenuParents, menuItems))
      }
    },

    activeSectionChanged(): void {
      this.title = this.$t(`graePage.${this.section}.title`).toString()
      this.subTitleIcon = ""
    },
  },
})
</script>
